/**
 * Canonical production origin and host checks.
 * Use this so that when the app runs on Vercel preview or any non-production host,
 * links and iframes point to production (cognidoor.com) instead of localhost or the preview URL.
 */

export const PRODUCTION_ORIGIN =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_PRODUCTION_ORIGIN) ||
  'https://www.cognidoor.com';

function readEnv(name) {
  if (typeof process === 'undefined' || !process.env) return undefined;
  return process.env[name];
}

/**
 * True when the current page is on the production domain (www.cognidoor.com or cognidoor.com).
 * Safe to call in browser; returns false during SSR/build.
 */
export function isProductionHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'www.cognidoor.com' || host === 'cognidoor.com';
}

/**
 * True when the current page is on localhost (dev). Safe for browser; false during SSR.
 */
export function isLocalHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1';
}

/**
 * Sub-app paths on production (same as proxy paths in dev).
 */
export const PRODUCTION_PATHS = {
  seawolfSimulator: '/seawolf-simulator',
  redrockApp: '/redrock-app',
  casey: '/casey',
  /** Live app mount (marketing pages stay at /mckinsey-ai-coach, /bcg-ai-coach). */
  mckinseyAICoach: '/apps/mckinsey-ai-coach',
  bcgAICoach: '/apps/bcg-ai-coach',
};

function joinUrl(baseUrl, path) {
  return `${baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Resolve a hosted standalone app URL:
 * - explicit env override wins
 * - localhost keeps the direct local dev server
 * - all other hosts fall back to the canonical production origin + subpath
 */
export function resolveHostedAppUrl({ envName, localhostUrl, productionPath }) {
  const envValue = readEnv(envName);
  if (envValue) return envValue;
  if (isLocalHost()) return localhostUrl;
  return joinUrl(PRODUCTION_ORIGIN, productionPath);
}

/**
 * Resolve an embedded coach/app URL:
 * - explicit env override wins
 * - production host can use same-origin subpath
 * - localhost keeps the direct local dev server
 * - preview/staging hosts fall back to canonical production origin + subpath
 */
export function resolveHostedEmbedUrl({ envName, localhostUrl, productionPath }) {
  const envValue = readEnv(envName);
  if (envValue) return envValue;
  if (isProductionHost()) return productionPath;
  if (isLocalHost()) return localhostUrl;
  return joinUrl(PRODUCTION_ORIGIN, productionPath);
}
